<template>
  <div>
    <!-- Header -->
    <div
      v-if="showHeader"
      class="dropdown-menu-header"
    >
      <div class="dropdown-header d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <h4 class="notification-title mb-0 mr-1">
            Notifications
          </h4>
          <b-badge
            v-if="unReadNotifications"
            pill
            variant="light-primary"
          >
            {{ unReadNotifications }} New
          </b-badge>
        </div>

        <a
          v-if="unReadNotifications"
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          block
          class="text-primary"
          @click="$bvModal.show('mark-all-as-read')"
        >Mark All as Read</a>
      </div>
    </div>

    <!-- Skeleton Loader -->
    <user-skeleton
      v-if="showSkeleton"
      class="ml-1"
      :count="3"
    />

    <!-- Notifications -->
    <vue-perfect-scrollbar
      v-else
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="div"
      style="height: 80vh; overflow: hidden"
      @scroll="fetchMoreNotification"
    >
      <!-- Account Notification -->
      <template v-if=" notifications.data && notifications.data.length">
        <a
          v-for="notification,i in notifications.data"
          :key="i"
          class="list-group-item p-0"
          @click="gotoNotification(notification,i)"
        >
          <component
            :is="notification.notification.__typename"
            :notification="notification"
            :un-read-notifications="unReadNotifications"
            @refetch="$emit('refetch')"
          />

        </a>
        <div class="text-center notification-card p-1 justify-content-center">
          <b-spinner v-if="isProcessing" />
        </div>
      </template>
      <p
        v-if="notifications.data && notifications.data.length==0"
        class="px-1 pt-1"
      >
        No New Notifications
      </p>
    </vue-perfect-scrollbar>
  </div>
</template>

<script>
import useApollo from '@/plugins/graphql/useApollo'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import { BBadge, BSpinner, BModal } from 'bootstrap-vue'
import UserSkeleton from '@/views/common/components/UserSkeleton.vue'
import NotificationInvoiceVerification from './notifications/NotificationInvoiceVerification.vue'
import NotificationManualTimeAction from './notifications/NotificationManualTimeAction.vue'
import NotificationTimeLimitChange from './notifications/NotificationTimeLimitChange.vue'
import NotificationTimeLimitCross from './notifications/NotificationTimeLimitCross.vue'
import NotificationFinalizePayment from './notifications/NotificationFinalizePayment.vue'
import NotificationWalletAction from './notifications/NotificationWalletAction.vue'
import NotificationBankWithdraw from './notifications/NotificationBankWithdraw.vue'
import NotificationPermissionUpdate from './notifications/NotificationPermissionUpdate.vue'
import NotificationUserStatusChangeInProject from './notifications/NotificationUserStatusChangeInProject.vue'
import NotificationPaidInvoiceVerification from './notifications/NotificationPaidInvoiceVerification.vue'
import NotificationWalletShareInvite from './notifications/NotificationWalletShareInvite.vue'
import NotificationWalletShareRevoke from './notifications/NotificationWalletShareRevoke.vue'
import NotificationWalletTransferAction from './notifications/NotificationWalletTransferAction.vue'
import TaskCompletedNotification from './notifications/TaskCompletedNotification.vue'
import TaskCreatedNotification from './notifications/TaskCreatedNotification.vue'
import TaskDueDateChangedNotification from './notifications/TaskDueDateChangedNotification.vue'
import BoardMemberAddedNotification from './notifications/BoardMemberAddedNotification.vue'
import BoardMemberRemovedNotification from './notifications/BoardMemberRemovedNotification.vue'
import TaskAssignedNotification from './notifications/TaskAssignedNotification.vue'
import TaskCommentMentionedNotification from './notifications/TaskCommentMentionedNotification.vue'
import TaskStatusChangedNotification from './notifications/TaskStatusChangedNotification.vue'
import TaskCreatedMentionNotification from './notifications/TaskCreatedMentionNotification.vue'
import TaskUpdatedMentionNotification from './notifications/TaskUpdatedMentionNotification.vue'
import getNotificationLink from './useNotification'

export default {
  components: {
    BBadge,
    BSpinner,
    BModal,
    NotificationManualTimeAction,
    NotificationTimeLimitChange,
    NotificationTimeLimitCross,
    NotificationFinalizePayment,
    NotificationWalletAction,
    NotificationInvoiceVerification,
    NotificationBankWithdraw,
    NotificationPermissionUpdate,
    NotificationUserStatusChangeInProject,
    NotificationPaidInvoiceVerification,
    NotificationWalletShareInvite,
    NotificationWalletShareRevoke,
    NotificationWalletTransferAction,
    TaskCompletedNotification,
    TaskStatusChangedNotification,
    TaskCreatedNotification,
    TaskDueDateChangedNotification,
    BoardMemberAddedNotification,
    BoardMemberRemovedNotification,
    TaskAssignedNotification,
    TaskCommentMentionedNotification,
    TaskCreatedMentionNotification,
    TaskUpdatedMentionNotification,
    UserSkeleton,
    VuePerfectScrollbar,
  },
  directives: {
    Ripple,
  },
  props: {
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      perfectScrollbarSettings: {
        wheelPropagation: false,
      },
      currentPage: 1,
      isProcessing: false,
      showSkeleton: false,
    }
  },
  computed: {
    notifications: {
      get() {
        return this.$store.state.notification.notifications
      },
      set(value) {
        this.$store.commit('notification/SET_NOTIFICATION', value)
      },

    },
    unReadNotifications: {
      get() {
        return this.$store.state.notification.unReadNotifications > 0 ? this.$store.state.notification.unReadNotifications : 0
      },

      set(val) {
        this.$store.commit('notification/SET_UNREAD_NOTIFICATIONS', val)
      },
    },
  },
  methods: {
    gotoNotification(notification, index) {
      if (!notification.isRead) {
        this.markAsRead(notification.uuid, index)
      }
      const link = getNotificationLink(notification)

      if ((!notification.link && !link) || this.$route.name === notification?.link || link === this.$route.path) return
      this.$emit('close-dropdown')

      if (!this.$route.hash && link.split('/')[1] === 'project-management') {
        this.$router.replace(`${link || notification.link}#`)
      } else this.$router.push(link || notification.link)
    },
    markAsRead(uuid, index) {
      if (!this.notifications.data[index].isRead) {
        useApollo.users.markNotificationAsRead({ uuid }).then(() => {
          this.notifications.data[index].isRead = true
          this.unReadNotifications -= 1
        })
      }
    },
    async fetchMoreNotification(e) {
      const { scrollTop, offsetHeight, scrollHeight } = e.target
      if (scrollHeight - (scrollTop + offsetHeight) < 5 && !this.isProcessing && this.notifications.hasMorePages) {
        const project = this.$store.state.project?.project
        this.isProcessing = true
        this.currentPage += 1
        await this.$store.dispatch('notification/fetchNotifications', { projectUid: project.uuid, page: this.currentPage })
        this.isProcessing = false
      }
    },
  },
}
</script>

<style lang="scss">
.notification-popover .dropdown-menu-header {
  border-top: 3px solid #3D61AD !important;
  border-radius: 6px 6px 0 0 !important;
}
</style>
